import {
  AloftArea,
  expected,
  isDefined,
  orUndefined,
  schAlertType,
  schAloftArea,
  schPairedCostRouteItem,
} from '@aloft/toolkit-shared';

import { CompPropertyDataState, SubjectPropertyDataState } from '../../../../redux/types.ts';
import { AlertDefinition } from '../../types.ts';
import { getAlertTriggers } from '../utils/alertTriggers.ts';
import { getCompTypeLabel, getScopedAlertType } from '../utils/compTypeScoping.ts';

export const bracketedGLA: AlertDefinition = ({
  compSequence,
  compType,
  propertyDataEntities,
  order,
}) => {
  const subjectGLA = orUndefined(schAloftArea.parse)(
    (propertyDataEntities.subject as SubjectPropertyDataState)?.subjectData.editableData
      .improvements.gla,
  );
  if (!subjectGLA) return undefined;

  const compGLAs = compSequence
    .map((id) =>
      orUndefined(schAloftArea.parse)(
        (propertyDataEntities[id] as CompPropertyDataState)?.compData.editableData.improvements.gla,
      ),
    )
    .filter(isDefined);

  if (compGLAs.length < 2) return undefined;

  const maxCompGLA = expected(AloftArea.max(compGLAs));
  const minCompGLA = expected(AloftArea.min(compGLAs));

  if (subjectGLA.le(maxCompGLA) && subjectGLA.ge(minCompGLA)) return undefined;

  const tag = compType ? ` by ${getCompTypeLabel(compType)} Comparables` : '';

  return {
    type: getScopedAlertType(schAlertType.enum.bracketedGLA, compType),
    status: 'active',
    severity: 'error',
    orderType: ['saas', 'appraisify'],
    message: `The Subject's GLA (${subjectGLA.toString()}) is not bracketed${tag} (${minCompGLA.toString()} - ${maxCompGLA.toString()}).`,
    propertyID: 'subject',
    ...(compType ? { compType } : {}),
    fixingInfo: {
      tips: "Tips: Expand your search parameters to locate sales that can bracket the Subject. This might mean going further back in time, expanding the search radius, or considering properties with other differences that can be reasonably adjusted. If the Subject's GLA cannot be bracketed, provide commentary on whether it is atypical for the market and any potential impact on marketability.",
      pairedCostRouteItem: schPairedCostRouteItem.enum.features,
      fixingMethod: ['comment', 'action'],
      commentAlwaysRequired: false,
      targetCellKey: 'subject-editableData.improvements.gla',
    },
    triggers: getAlertTriggers({ fixRoute: schPairedCostRouteItem.enum.features, order }),
  };
};
